// Retry wrapper for GoTrue's token endpoint.
//
// The auth service answered one refresh with:
//   500 "error finding refresh token: failed to connect to host=localhost
//        user=supabase_auth_admin ... dial tcp [::1]:5432: operation was canceled"
// That is Supabase-side infrastructure. It is NOT a signed-out user, so the
// refresh is retried with backoff instead of being allowed to end the session.
//
// Only POST /auth/v1/token is retried, and only on 5xx / network failure.
// 400 and 401 (invalid_grant) fall straight through: those really are dead
// sessions. An aborted request is never retried.

export const TOKEN_RETRY_BACKOFF_MS = [500, 1500, 3500];

const isTokenRefresh = (url: string, init?: RequestInit) =>
  url.includes('/auth/v1/token') &&
  String(init?.method ?? 'GET').toUpperCase() === 'POST' &&
  url.includes('grant_type=refresh_token');

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

export function makeAuthRetryFetch(
  baseFetch: typeof fetch = (...a: Parameters<typeof fetch>) => fetch(...a),
  { backoff = TOKEN_RETRY_BACKOFF_MS, wait = sleep } = {}
): typeof fetch {
  return async function authRetryFetch(input: any, init?: any): Promise<Response> {
    const url = typeof input === 'string' ? input : (input?.url ?? String(input));
    if (!isTokenRefresh(url, init)) return baseFetch(input, init);

    let lastError: any = null;

    for (let attempt = 0; attempt <= backoff.length; attempt++) {
      try {
        const res = await baseFetch(input, init);
        if (res.status < 500 || attempt === backoff.length) return res;
        console.warn(
          `[auth-exit] token refresh got ${res.status} from the auth service — retrying in ${backoff[attempt]}ms (not a logout)`
        );
      } catch (err: any) {
        // A navigation aborted it. Expected; hand it back untouched.
        if (err?.name === 'AbortError') throw err;
        lastError = err;
        if (attempt === backoff.length) throw err;
        console.warn(
          `[auth-exit] token refresh network error — retrying in ${backoff[attempt]}ms (not a logout)`,
          err
        );
      }
      await wait(backoff[attempt]);
    }

    throw lastError ?? new Error('token refresh failed');
  } as typeof fetch;
}
